import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, UserPlus, MessageCircle, CornerDownRight, Bell, CheckCheck } from "lucide-react";
import api, { mediaUrl, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";

const ICONS = { like: Heart, follow: UserPlus, comment: MessageCircle, reply: CornerDownRight };

export default function Notifications() {
  const { refreshNotifications } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => setData({ items: [], unread: 0 }));
  }, []);

  const markRead = async () => {
    await api.post("/notifications/read");
    setData((d) => ({ ...d, items: d.items.map((n) => ({ ...n, read: true })), unread: 0 }));
    refreshNotifications();
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow text-primary">Atividades</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Notificações</h1>
        </div>
        {data?.items?.length > 0 && (
          <Button data-testid="mark-read-btn" variant="outline" size="sm" onClick={markRead} className="rounded-full gap-1.5"><CheckCheck className="h-4 w-4" /> Marcar como lidas</Button>
        )}
      </div>

      <div className="mt-6 space-y-2">
        {data === null ? <Loader /> : data.items.length === 0 ? (
          <EmptyState icon={Bell} title="Nenhuma notificação ainda." subtitle="Curtidas, comentários e novos seguidores aparecerão aqui." />
        ) : data.items.map((n) => {
          const Icon = ICONS[n.type] || Bell;
          const inner = (
            <div data-testid="notification-item" className={`flex items-center gap-3 rounded-xl border p-4 transition ${n.read ? "border-border bg-card" : "border-primary/30 bg-primary/5"}`}>
              <div className="relative">
                <img src={mediaUrl(n.actor?.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
                <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"><Icon className="h-3 w-3" /></span>
              </div>
              <div className="flex-1">
                <p className="text-sm"><span className="font-semibold">{n.actor?.name}</span> {n.message}</p>
                <p className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</p>
              </div>
              {!n.read && <span className="h-2 w-2 rounded-full bg-primary" />}
            </div>
          );
          return n.project_id ? <ProjectLink key={n.id} projectId={n.project_id}>{inner}</ProjectLink> : <div key={n.id}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

function ProjectLink({ projectId, children }) {
  const [slug, setSlug] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}`).then((r) => setSlug(r.data.slug)).catch(() => {}); }, [projectId]);
  return slug ? <Link to={`/projeto/${slug}`}>{children}</Link> : <div>{children}</div>;
}
